/**
 * Який вигляд має збій (UX-50) і який код показати людині. Без React і без залежностей від
 * клієнтів API: помилку розпізнаємо за формою (`status`, `code`, `traceId` у тілі), як у
 * `api/temporary.ts`, бо їх дві: `ApiError` (`api/client.ts`) і `ApiProblem` (платформа).
 *
 * Вид збою для сторінок помилок (`failureKind`) — у `failure-kind.ts` (лінивий чанк).
 */

export interface Shaped {
  status?: unknown;
  code?: unknown;
  retryAfter?: unknown;
  body?: unknown;
}

export const shaped = (error: unknown): Shaped => (error && typeof error === 'object' ? error : {});

/** Код з відповіді сервера — лише безпечні знаки: потрапляє на екран і у звернення. */
const SERVER_CODE = /^[A-Za-z][A-Za-z0-9_.:-]{0,47}$/;
/** `traceId` — ідентифікатор запиту; edge ставить UUID, але заголовок міг прийти від клієнта. */
const TRACE_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{5,63}$/;

/** Збій запиту до сервера (є HTTP-статус), а не виняток у нашому коді. */
export function isRequestFailure(error: unknown): boolean {
  return typeof shaped(error).status === 'number';
}

/**
 * Код для підтримки. Клієнтські коди (`REC-404`, `NET-000`) лишаються як є; код Problem Details
 * (`forbidden`, `stale_version`) — у верхньому регістрі з дефісами (`STALE-VERSION`); невідоме або
 * небезпечне — `HTTP-<статус>` чи `APP-000`. Стек, текст помилки й `detail` сюди не потрапляють.
 */
export function supportCode(error: unknown): string {
  const { status, code } = shaped(error);
  if (status === 0) return 'NET-000';
  if (typeof code === 'string' && SERVER_CODE.test(code)) {
    return /^[A-Z]{2,5}-\d{3}$/.test(code) ? code : code.toUpperCase().replaceAll(/[_.:]/g, '-');
  }
  return typeof status === 'number' && status >= 100 && status <= 599
    ? `HTTP-${status}`
    : 'APP-000';
}

/** Короткий `traceId` з Problem Details, якщо він схожий на ідентифікатор; інакше `null`. */
export function traceIdOf(error: unknown): string | null {
  const body = shaped(shaped(error).body);
  const trace = (body as { traceId?: unknown }).traceId;
  return typeof trace === 'string' ? cleanTraceId(trace) : null;
}

/** Код для підтримки в тому вигляді, який показуємо і надсилаємо (`REC-404`, `STALE-VERSION`). */
const SUPPORT_CODE = /^[A-Z][A-Z0-9-]{1,47}$/;

/** Код лише допустимої форми; інакше `null`: у звернення й журнал помилок нічого іншого не йде. */
export function cleanCode(value: string | null | undefined): string | null {
  return value && SUPPORT_CODE.test(value) ? value : null;
}

/** `traceId` лише з безпечних знаків і не довший за 64; інакше `null` (ні в звернення, ні на екран). */
export function cleanTraceId(value: string | null | undefined): string | null {
  return value && TRACE_ID.test(value) ? value : null;
}
